import { useState, useEffect, useCallback } from 'react';
import { AvailablePlayer } from './TeamSubmission';

interface UsePlayerListsProps {
  devPort: string;
  clubId: number;
  selectedLeague: number | null;
  refreshProps?: number;
}

interface UsePlayerListsReturn {
  availablePlayers: AvailablePlayer[];
  unavailablePlayers: number[];
  loadingPlayers: boolean;
}

export const usePlayerAvailabilityList = ({ devPort, clubId, selectedLeague, refreshProps }: UsePlayerListsProps): UsePlayerListsReturn => {
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [unavailablePlayers, setUnavailablePlayers] = useState<number[]>([]);

  const getUnavailablePlayers = useCallback(() => {
    if (!selectedLeague) return;
    
    const fetchTeams = `${devPort}tournament_team_list/${clubId}/${selectedLeague}`;
    fetch(fetchTeams)
      .then((response) => response.json())
      .then((data) => {
        let unavailablePlayers: number[] = [];
        if (data.length > 0) {
          data.forEach((player: any) => {
            //If this is not a visiting player, add the id
            if (!player[7] && player[12] === 1) {
              unavailablePlayers.push(player[2]);
            }
          });
        }
        setUnavailablePlayers(unavailablePlayers);
      });
  }, [devPort, clubId, selectedLeague]);

  useEffect(() => {
    getUnavailablePlayers();
  }, [selectedLeague, getUnavailablePlayers, refreshProps]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(
          `${devPort}player_list/${clubId}/False`
        );
        const data = await response.json();

        // Create a Map to store unique players by their ID
        const uniquePlayersMap = new Map();
        
        data.forEach((player: any[]) => {
          const playerId = player[4];
          // Only add if we haven't seen this player ID before
          if (!uniquePlayersMap.has(playerId)) {
            uniquePlayersMap.set(playerId, {
              label: `${player[0]} ${player[1]}`,
              value: playerId,
            });
          }
        });

        // Convert Map values back to array
        const formattedPlayers = Array.from(uniquePlayersMap.values());
        setAvailablePlayers(formattedPlayers);
        setLoadingPlayers(false);
      } catch (error) {
        console.error("Error fetching players:", error);
        setLoadingPlayers(false);
      }
    };

    fetchPlayers();
  }, [devPort, clubId, refreshProps]);

  return {
    availablePlayers,
    unavailablePlayers,
    loadingPlayers,
  };
}; 